import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CalendarDays, ArrowLeft, Phone } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d) ? "" : d.toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" });
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(undefined); // undefined=loading, null=not found
  const [company, setCompany] = useState({});

  useEffect(() => {
    api.get("/content").then((r) => setCompany(r.data.settings?.company || {})).catch(() => {});
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setArticle(null));
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    const prevImg = document.head.querySelector('meta[property="og:image"]')?.getAttribute("content");
    const prevW = document.head.querySelector('meta[property="og:image:width"]')?.getAttribute("content");
    const prevH = document.head.querySelector('meta[property="og:image:height"]')?.getAttribute("content");
    document.title = `${article.title} | SIMSERV Slobozia`;
    const url = `https://simserv.ro/articole/${article.slug}`;
    const ogImage = `${window.location.origin}/api/og/${article.slug}.jpg`;

    const upsert = (selector, attr, key, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setName = (name, content) => upsert(`meta[name="${name}"]`, "name", name, content);
    const setProp = (prop, content) => upsert(`meta[property="${prop}"]`, "property", prop, content);

    setName("description", article.excerpt || article.title);
    setProp("og:type", "article");
    setProp("og:title", article.title);
    setProp("og:description", article.excerpt || article.title);
    setProp("og:url", url);
    // Always a guaranteed 1200x630 JPEG rendered server-side from the cover, so
    // WhatsApp/Facebook previews work automatically for every article (incl. new
    // ones), no matter the uploaded cover's real dimensions.
    setProp("og:image", ogImage);
    setProp("og:image:secure_url", ogImage);
    setProp("og:image:width", "1200");
    setProp("og:image:height", "630");
    setProp("og:image:type", "image/jpeg");
    setName("twitter:card", "summary_large_image");
    setName("twitter:title", article.title);
    setName("twitter:description", article.excerpt || article.title);
    setName("twitter:image", ogImage);

    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", url);

    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: [ogImage],
      datePublished: article.created_at,
      dateModified: article.updated_at,
      author: { "@type": "Organization", name: "SIMSERV Auto Service" },
      publisher: { "@type": "Organization", name: "SIMSERV Auto Service" },
      mainEntityOfPage: url,
    };
    const prev = document.getElementById("article-jsonld");
    if (prev) prev.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-jsonld";
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById("article-jsonld");
      if (s) s.remove();
      document.title = prevTitle;
      if (prevImg) setProp("og:image", prevImg);
      if (prevW) setProp("og:image:width", prevW);
      if (prevH) setProp("og:image:height", prevH);
    };
  }, [article]);

  if (article === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  if (article === null)
    return (
      <div className="min-h-screen bg-[#F4F6F9]">
        <Navbar company={company} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-[#0F1E36]">Articol inexistent</h1>
          <p className="text-slate-500 mt-3">Articolul căutat nu există sau a fost șters.</p>
          <Link to="/articole" className="inline-flex items-center gap-2 mt-6 text-[#FF5A1F] font-semibold">
            <ArrowLeft className="w-4 h-4" /> Înapoi la articole
          </Link>
        </div>
        <Footer company={company} />
      </div>
    );

  const phone = (company.phone || "").replace(/\s/g, "");

  return (
    <div className="min-h-screen bg-white" data-testid="article-detail-page">
      <Navbar company={company} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        <Link to="/articole" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#FF5A1F] mb-6 transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> Toate articolele
        </Link>

        <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
          <CalendarDays className="w-3.5 h-3.5" />
          {fmtDate(article.created_at)}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#0F1E36] leading-tight">
          {article.title}
        </h1>
        {article.excerpt && (
          <p className="text-lg text-slate-500 mt-4">{article.excerpt}</p>
        )}

        {article.cover_image && (
          <div className="mt-6 rounded-2xl overflow-hidden aspect-[16/9] bg-[#F4F6F9]">
            <img src={resolveImg(article.cover_image)} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div
          className="article-body mt-8 text-[#1F2937]"
          dangerouslySetInnerHTML={{ __html: article.content || "" }}
        />

        <div className="mt-12 rounded-2xl bg-[#0F1E36] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold">Ai nevoie de ajutor cu mașina?</h3>
            <p className="text-white/70 text-sm mt-1">Programează-te la SIMSERV Slobozia — te ajutăm rapid și corect.</p>
          </div>
          {phone && (
            <a href={`tel:${phone}`} className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold px-5 py-3 rounded-lg transition-colors duration-200 shrink-0">
              <Phone className="w-4 h-4" /> {company.phone}
            </a>
          )}
        </div>
      </article>
      <Footer company={company} />
    </div>
  );
}
